/**
 * Cart Notification upsell product
 */
class UpsellProduct extends HTMLElement {
  constructor() {
    super();

    // Selectors
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.cartNotification = document.querySelector('cart-notification');
    this.cartItems = this.cartNotification?.querySelector('cart-items');
    this.closeButton = this.querySelector('[data-close-button]');

    // Properties
    this.refProductId = this.dataset.refProductId;
    this.refLineItem = this.dataset.refLineItem;
    this.refPartition = this.dataset.refPartition;
    this.refProductWidth = this.dataset.refWidth;
    this.productType = this.dataset.productType;
    this.refParts = this.refPartition == 'couple' ? 2 : 1;
    this.refLineEl = this.cartNotification.querySelector(
      `[data-product-ref="${this.refLineItem}"]`
    );
    this.refQuantity = this.refLineEl?.querySelector('quantity-input input')?.value || 1;
    this.quantity =
      this.productType == 'draw-rod' ? this.refQuantity * this.refParts : this.refQuantity;

    // Get translations
    this.translations = parseJson(this.dataset.translations);

    // Event listeners for product without variants
    this.querySelector('[data-add-to-cart]')?.addEventListener('click', (evt) => {
      evt.preventDefault();
      const id = evt.currentTarget.dataset.addToCart;
      this.addToCart(id);
    });

    // Event listeners for product with variants
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.form?.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', this.onVariantChange.bind(this));
    });
  }

  /**
   * on Variant Change
   * @param {Event} evt
   */
  onVariantChange(evt) {
    this.updateOptions();
    this.updateMasterId();
    this.updateProperties();

    if (this.currentVariant && this.hasAllProperties) {
      this.toggleSubmitButton(false);
    } else {
      this.toggleSubmitButton(true);
    }
  }

  /**
   * onSubmit
   * @param {Event} evt
   */
  onSubmit(evt) {
    evt.preventDefault();
    if (!this.currentVariant || !this.hasAllProperties) {
      return;
    }
    this.closeButton.dispatchEvent(new Event('click'));
    this.addToCart(this.currentVariant.id);
  }

  /**
   * Get variant data
   */
  getVariantData() {
    this.variantData =
      this.variantData || parseJson(this.querySelector('[data-upsell-product-json]').textContent);
    return this.variantData;
  }

  /**
   * Update options
   */
  updateOptions() {
    // Get all options lists
    const lists = Array.from(this.querySelectorAll('[data-option-list]'));

    const options = lists.map((fieldset) => {
      const input = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      return input
        ? {
            name: input.name,
            value: input.value,
            label: input.dataset.label == 'false' ? null : input.dataset.label,
          }
        : null;
    });

    // Get all values as array
    this.options = options.map((option) => (option ? option.value : null));

    // Merge all options into one properties object
    this.propertiesOptions = {};
    options
      .filter((option) => option && option.label)
      .map((option) => {
        this.propertiesOptions = {
          ...this.propertiesOptions,
          [option.label]: option.value,
        };
      });
  }

  /**
   * Update properties
   */
  updateProperties() {
    // Get all properties lists
    const lists = Array.from(this.querySelectorAll('[data-property-list]'));

    // Check if there are any properties lists
    this.hasProperties = lists.length ? true : false;

    // Get selected properties with key and value
    const properties = lists.map((list) => {
      const input = Array.from(list.querySelectorAll('input')).find((radio) => radio.checked);
      return input
        ? {
            [input.name]: input.value,
          }
        : null;
    });

    // Check if for each property list a property is selected
    this.hasAllProperties = properties.filter((property) => property).length === lists.length;

    // Merge all properties into one object
    this.properties = {};
    properties.map((property) => {
      this.properties = {
        ...this.properties,
        ...property,
      };
    });
  }

  /**
   * Update master ID / selected variant
   */
  updateMasterId() {
    // Check if all options are selected
    if (this.options.filter((option) => !option).length) {
      this.currentVariant = null;
      return;
    }

    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.options[index]) {
            return true;
          }
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  /**
   * renderContents
   * @param {int} - product id
   */
  async addToCart(id) {
    if (!id) {
      return;
    }

    // Set loading state
    this.cartItems.enableLoading(this.cartNotification);

    // Get reference product quantity
    this.refQuantity = this.refLineEl?.querySelector('quantity-input input')?.value || 1;

    // Draw rods are sold times partition
    this.quantity =
      this.productType == 'draw-rod' ? this.refQuantity * this.refParts : this.refQuantity;

    // Delivery time
    const delivery_time = this.querySelector('[data-delivery-time]')?.value || null;

    try {
      // Add to cart body
      const body = JSON.stringify({
        items: [
          {
            id: id,
            quantity: this.quantity,
            properties: {
              _ref: new Date().getTime(),
              _refProductId: this.refProductId,
              _refLineItem: this.refLineItem,
              _refWidth: this.refProductWidth,
              _refPartition: this.refPartition,
              _refParts: this.refParts,
              _refType: this.productType,
              _refQuantity: this.refQuantity,
              _upsell: true,
              ...(delivery_time && {
                _delivery_time: delivery_time,
              }),
              ...this.propertiesOptions,
              ...this.properties,
            },
          },
        ],
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });

      // Add to cart request
      const addToCartResponse = await fetch(`${routes.cart_add_url}`, {
        ...fetchConfig(),
        ...{ body },
      });
      const addToCartData = await addToCartResponse.json();

      // Get product data
      if (!addToCartData.items) {
        throw new Error('No items added to the cart');
      }

      // Get product data
      const productData = addToCartData.items[0];

      // Show notification
      this.renderContents(addToCartData);

      // Hide the upsell product because it's added to the cart
      this.toggleVisible(false);

      // Remove loading state
      this.cartItems.disableLoading(this.cartNotification);

      // Push to Shopify analytics pixel
      if (Shopify?.analytics) {
        const dataLayer = window.dataLayer || [];

        // Push to dataLayer
        dataLayer.push({
          event: 'upsell_added_to_cart',
          price: productData.price,
          currency: Shopify?.currency?.active || 'EUR',
          product_title: productData.product_title,
          product_type: productData.properties._refType,
          quantity: productData.quantity,
          total_cost: productData.total_price,
        });

        // Push to Shopify analytics
        Shopify.analytics.publish('upsell_added_to_cart', {
          price: productData.price,
          currency: Shopify?.currency?.active || 'EUR',
          product_title: productData.product_title,
          product_type: productData.properties._refType,
          quantity: productData.quantity,
          total_cost: productData.total_price,
        });
      }
    } catch (error) {
      console.error(error);

      // Remove loading state
      this.cartItems.disableLoading(this.cartNotification);
    }
  }

  /**
   * Toggle if this element is visible or not
   * @param {*} visible
   */
  toggleVisible(visible = true) {
    if (visible) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    // Check if even one upsell product is visible
    const upsellProducts = document.querySelectorAll('upsell-product');
    const visibleUpsellProducts = Array.from(upsellProducts).filter((upsellProduct) => {
      return !upsellProduct.classList.contains('hidden');
    });

    // Show or hide upsell cart notification
    if (visibleUpsellProducts.length) {
      document.querySelector('[data-cart-notification-upsell]').classList.remove('hidden');
    } else {
      document.querySelector('[data-cart-notification-upsell]').classList.add('hidden');
    }
  }

  /**
   * Toggle submit button
   * @param {Boolean} disable
   */
  toggleSubmitButton(disable = true) {
    if (disable) {
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.textContent = this.translations.submit_button_text_disabled;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.textContent = this.translations.submit_button_text;
    }
  }

  /**
   * renderContents
   * @param {Object} parsedState
   */
  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      // Add added upsell cart item to cart notification
      if (section.id == 'cart-notification-items') {
        this.cartNotification.addLineItem(parsedState);
        this.cartNotification.updateQuantityState();
        this.cartNotification.renderTotals();
        return;
      }
      // Update all other sections
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });
  }

  /**
   * getSectionInnerHTML
   * @param {String} String that contains html
   * @param {String} selector
   * @returns
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * getSectionsToRender
   * @returns {Array} Sections to render
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-button',
        section: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
      {
        id: 'cart-notification-items',
        section: 'cart-notification-items',
        selector: `#cart-notification-items`,
      },
    ];
  }
}

if (!customElements.get('upsell-product')) {
  customElements.define('upsell-product', UpsellProduct);
}
